import { useEffect, useRef } from 'react';

/**
 * 레퍼런스 재생 — 화면이 열라고 할 때 **처음부터** 튼다.
 *
 * 재생 시작 시각이 채점의 시간 기준(anchor)이다: 멜로디의 `startMs`는 음원 t=0 기준이고
 * 프레임의 `timestampMs`는 캡처 타임라인 기준이라, 둘을 잇는 건 "재생을 언제 걸었나"뿐이다
 * (songScoring 모듈 주석). 그래서 여기서는 `playing`이 켜지는 순간 **되감아서** 튼다 —
 * 이어 재생하면 앵커가 조용히 어긋난다. *언제* 켜는지는 부모가 정한다.
 *
 * 소리는 스피커로 나가고 마이크는 그걸 같이 듣는다. 에코 제거는 기기 몫이라 레퍼런스가
 * 그대로 f0로 잡힐 수 있다 — 이 컴포넌트가 막을 수 있는 문제가 아니고, 채점이 그 사실을
 * 안고 돈다(songScoring 한계 주석). iOS AEC(`setVoiceProcessingEnabled`)로 지워 봤지만
 * 음높이 추정이 깨져 철회했다(2026-09-16, `MuzlyAudioCapture.mm` 주석).
 *
 * **WebAudio 게인은 쓰지 않는다** (2026-09-16 제거). 네이티브 셸에서 출력에 4배 게인을 걸던
 * 우회가 있었는데, 그것은 캡처 세션이 `.measurement`로 서서 출력이 깎이던 시절의 처방이다.
 * 노래 테이크는 지금 `.default`라 출력이 안 깎이고, 4배는 스피커만 키워 마이크로 더 크게
 * 되돌아왔다 — 실기기에서 입력 rms가 -18.5dBFS까지 올라 음성 게이트가 방을 아예 못 쟀다
 * (`floor=OPEN`, `oct=114`). 엘리먼트를 `AudioContext`에 물리면 iOS가 그 그래프를 재울 때
 * 반주가 통째로 사라지는 문제도 같이 있었다(2026-09-10).
 */

interface SongReferenceAudioProps {
  /** 미리 받아 둔 음원(objectURL) — 여기서 처음 네트워크를 타지 않는다. */
  src: string;
  playing: boolean;
}

export const SongReferenceAudio = ({
  src,
  playing,
}: SongReferenceAudioProps) => {
  const ref = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = ref.current;
    if (!audio) {
      return;
    }
    if (!playing) {
      audio.pause();
      return;
    }
    audio.currentTime = 0;
    // 사용자가 "시작"을 누른 흐름 안이라 자동재생 정책에 걸리지 않는다. 그래도 실패할 수
    // 있는 경로(오디오 포커스 상실 등)라 삼키되, 녹음은 계속 간다 — 화면을 멈춰 세우면
    // 이미 잰 두 진단까지 잃는다.
    void audio.play().catch(() => undefined);
  }, [playing, src]);

  return <audio ref={ref} src={src} preload="auto" aria-hidden="true" />;
};
